/**
 * norse-usps-small.js
 * Hides any list item containing "free shipping" text when the current
 * bundle/product price is below the free shipping threshold.
 * Listens to norse-bundles-buy-box:change and variant:change for dynamic updates.
 *
 * Also replaces dynamic price/time tokens in USP list items for
 * norse-subscription-buy-box plans. Supported tokens:
 *   {dynamic_compare_at} / {dymanic_compare_at}  — compare-at for the refill interval (line total)
 *   {dynamic_price}                               — ongoing refill price for the interval (line total, like ritual rv-bl)
 *   {dynamic time}                                — delivery interval from plan_N_months, e.g. "3 Months"
 */
(function () {
  function parseMoney(str) {
    return parseFloat(String(str || '').replace(/[^\d.]/g, '')) || 0;
  }

  function centsFromEl(el) {
    if (!el) return 0;
    const raw = el.textContent.trim();
    return Math.round(parseMoney(raw) * 100);
  }

  /**
   * For each .norse-usps-small block, hide/show "free shipping" list items
   * based on whether the given priceCents >= threshold.
   */
  function updateFreeShippingItems(block, priceCents) {
    const threshold = parseInt(block.dataset.freeShippingThreshold || '0', 10);

    // A market with no free-shipping rate at all (Mexico) reports enabled:false
    // and a zero threshold. Zero used to mean "not configured → always show",
    // which promised free shipping that checkout can never honour. Hide it.
    // refreshDynamicTokens() restores innerHTML from cache, so this has to be
    // re-applied on every pass rather than trusted to stick.
    const st = window.NorseShippingThreshold;
    const disabled = !!(st && st.enabled === false);
    if (!threshold && !disabled) return; // genuinely unconfigured → always show

    block.querySelectorAll('ul li').forEach(function (li) {
      const text = li.textContent.toLowerCase();
      if (text.includes('free') && text.includes('ship')) {
        li.classList.toggle('norse-usps-small__item--hidden', disabled || priceCents < threshold);
      }
    });
  }

  /**
   * Resolve the current displayed price in cents from the submit button label
   * (most reliable single source of truth across buy-box variants).
   */
  function getCurrentPriceCents() {
    // 1. Try bundles buy box price
    const nbbb = document.querySelector('norse-bundles-buy-box');
    if (nbbb) {
      const activeCard = nbbb.querySelector('.nbb__variant-option[data-nbbb-proposal-idx].is-selected');
      if (activeCard) {
        const idx = nbbb.selectedQtyIndex ?? 0;
        const subKey = idx === 1 ? 'nbbbSubQ1' : idx === 2 ? 'nbbbSubQ2' : 'nbbbSubQ0';
        const cents = parseInt(activeCard.dataset[subKey] || activeCard.dataset.nbbbSubQ0 || '0', 10);
        if (cents > 0) return cents;
      }
    }

    // 2. Norse subscription buy box — line total for selected plan
    const nsbbOpt = document.querySelector('[data-nsbb-option].is-selected');
    if (nsbbOpt) {
      const line = parseInt(nsbbOpt.dataset.subLine || '0', 10);
      if (line > 0) return line;
    }

    // 3. Try submit button label for price text like "ADD TO CART - $79"
    const submitBtn = document.querySelector('[id^="ProductSubmitButton-"]');
    if (submitBtn) {
      const span = submitBtn.querySelector('span');
      if (span) {
        const m = span.textContent.match(/\$[\d,]+(?:\.\d+)?/);
        if (m) return Math.round(parseMoney(m[0]) * 100);
      }
    }

    return 0;
  }

  function refreshAll() {
    const priceCents = getCurrentPriceCents();
    document.querySelectorAll('.norse-usps-small[data-free-shipping-threshold]').forEach(function (block) {
      updateFreeShippingItems(block, priceCents);
    });
  }

  // ─────────────────────────────────────────────────────────────
  //  Dynamic price/time token replacement for nsbb plans
  // ─────────────────────────────────────────────────────────────

  /** Exact cents in the market's punctuation — see NorseSellingPlanUtils.formatMoney. */
  function formatMoney(cents) {
    var shared = window.NorseSellingPlanUtils && window.NorseSellingPlanUtils.formatMoney;
    if (shared) return shared(cents);
    if (window.NorseShippingThreshold && typeof window.NorseShippingThreshold.formatMoney === 'function') {
      return window.NorseShippingThreshold.formatMoney(cents);
    }
    return (Math.round(Number(cents) || 0) / 100).toFixed(2).replace(/\.00$/, '');
  }

  function formatMonths(months) {
    var n = parseInt(months, 10) || 1;
    return n === 1 ? '1 Month' : n + ' Months';
  }

  /**
   * Cache the raw HTML of each USP block that contains tokens (first call only),
   * so subsequent updates can re-apply replacements cleanly.
   */
  var _uspsOriginals = new WeakMap();

  function getOriginalHtml(block) {
    if (!_uspsOriginals.has(block)) {
      _uspsOriginals.set(block, block.innerHTML);
    }
    return _uspsOriginals.get(block);
  }

  var TOKEN_RE = /\{(?:dymanic_compare_at|dynamic_compare_at|dynamic_price|dynamic time)\}/gi;

  function hasTokens(html) {
    return TOKEN_RE.test(html);
  }

  function replaceTokens(html, compareAt, price, time) {
    return html.replace(/\{dymanic_compare_at\}/gi, compareAt)
               .replace(/\{dynamic_compare_at\}/gi, compareAt)
               .replace(/\{dynamic_price\}/gi, price)
               .replace(/\{dynamic time\}/gi, time);
  }

  function refreshDynamicTokens() {
    var selOpt = document.querySelector('[data-nsbb-option].is-selected');
    if (!selOpt) return;

    var months = parseInt(selOpt.dataset.months || '1', 10) || 1;
    var recurringLine = parseInt(selOpt.dataset.recurringLine || '0', 10);
    var cmpLine = parseInt(selOpt.dataset.cmpLine || '0', 10);

    if (!recurringLine) {
      var recurringPm = parseInt(selOpt.dataset.recurringPm || '0', 10);
      var subPm = parseInt(selOpt.dataset.subPm || '0', 10);
      recurringLine = recurringPm * months || subPm * months || parseInt(selOpt.dataset.subLine || '0', 10);
    }
    if (!cmpLine) {
      var cmpPm = parseInt(selOpt.dataset.cmpPm || '0', 10);
      cmpLine = cmpPm * months;
    }
    if (!recurringLine) return;

    var priceStr = formatMoney(recurringLine);
    var compareStr = '';
    if (cmpLine > recurringLine) {
      compareStr = formatMoney(cmpLine);
    }
    // Interval from selected plan row (plan_N_months), not shared selling plan name
    var timeStr = formatMonths(months);

    document.querySelectorAll('.norse-usps-small').forEach(function (block) {
      var original = getOriginalHtml(block);
      if (!hasTokens(original)) return;
      block.innerHTML = replaceTokens(original, compareStr, priceStr, timeStr);
    });
  }

  // ─────────────────────────────────────────────────────────────
  //  Init
  // ─────────────────────────────────────────────────────────────

  function init() {
    refreshAll();
    refreshDynamicTokens();

    // Re-evaluate when bundles buy box changes proposal or qty
    document.addEventListener('norse-bundles-buy-box:change', function () {
      refreshAll();
      refreshDynamicTokens();
    });

    // Re-evaluate on standard variant change
    document.addEventListener('variant:change', function (e) {
      var variantPrice = e.detail && e.detail.price;
      if (variantPrice != null) {
        var cents = parseInt(variantPrice, 10);
        document.querySelectorAll('.norse-usps-small[data-free-shipping-threshold]').forEach(function (block) {
          updateFreeShippingItems(block, cents);
        });
      } else {
        refreshAll();
      }
      refreshDynamicTokens();
    });

    document.addEventListener('nsbb:plan-change', function () {
      refreshAll();
      refreshDynamicTokens();
    });

    // Re-evaluate when nsbb plan option changes (keyboard / click)
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-nsbb-option]')) {
        requestAnimationFrame(refreshDynamicTokens);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
